/**
 * Google Agenda da PESSOA logada.
 *
 * Fica na aba Disponibilidade porque é dela que a conexão trata: o que está
 * ocupado no Google da pessoa vira bloqueio aqui, e as reuniões em que ela
 * participa aparecem no Google dela. Não é integração da empresa — cada um
 * conecta a sua, e só a sua.
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarCheck, CheckCircle2, XCircle, RefreshCw, Loader2, Unplug, Info } from "lucide-react";
import GoogleCalendarIcon from "@/components/Agenda/GoogleCalendarIcon";
import { toast } from "sonner";

type Conectado = {
  googleEmail: string | null;
  calendarId: string;
  showEventTitles: boolean;
  lastSyncAt: Date | string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
};

export default function GoogleCalendarCard() {
  const utils = trpc.useUtils();
  const status = trpc.googleCalendar.status.useQuery();
  const s = status.data;

  const invalidar = () => {
    utils.googleCalendar.status.invalidate();
    utils.agenda.blocks.list.invalidate();
  };

  const syncNow = trpc.googleCalendar.syncNow.useMutation({
    onSuccess: (r) => {
      const b = r.bloqueios;
      toast.success(
        `Sincronizado — ${b.criados + b.atualizados + b.removidos > 0
          ? `${b.criados} novos, ${b.atualizados} atualizados, ${b.removidos} removidos`
          : "nada mudou"}`,
      );
      invalidar();
    },
    onError: (e) => {
      toast.error(toUserFriendlyError(e, "Falha ao sincronizar"));
      invalidar();
    },
  });
  const disconnect = trpc.googleCalendar.disconnect.useMutation({
    onSuccess: () => {
      toast.success("Google Agenda desconectado");
      invalidar();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Falha ao desconectar")),
  });
  const updateSettings = trpc.googleCalendar.updateSettings.useMutation({
    onSuccess: () => {
      toast.success("Opções salvas — sincronizando…");
      invalidar();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Falha ao salvar")),
  });

  const conectar = () => {
    window.location.href = "/api/integrations/google-calendar/connect";
  };

  return (
    <Card className="bg-white dark:bg-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GoogleCalendarIcon className="h-4 w-4" />
          Meu Google Agenda
        </CardTitle>
        <CardDescription>
          Seus compromissos do Google aparecem aqui como horário ocupado, e as reuniões marcadas no
          LeadConversa vão para o seu Google. Só você vê e conecta a sua conta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.isLoading || !s ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : !s.connected ? (
          <div className="space-y-3">
            {!s.configured ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-sm text-amber-900 dark:text-amber-200 flex gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  A conexão com o Google ainda não está configurada neste ambiente (
                  <code className="text-xs">GOOGLE_CLIENT_ID</code> / <code className="text-xs">GOOGLE_CLIENT_SECRET</code>).
                </span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhuma conta conectada. Ao conectar, os próximos 60 dias do seu calendário são trazidos e
                mantidos em dia automaticamente.
              </p>
            )}
            <Button onClick={conectar} disabled={!s.configured}>
              <CalendarCheck className="h-4 w-4 mr-1.5" /> Conectar meu Google Agenda
            </Button>
          </div>
        ) : (
          <ConectadoView s={s} syncNow={syncNow} disconnect={disconnect} updateSettings={updateSettings} onReconectar={conectar} />
        )}
      </CardContent>
    </Card>
  );
}

function ConectadoView({
  s,
  syncNow,
  disconnect,
  updateSettings,
  onReconectar,
}: {
  s: Conectado;
  syncNow: ReturnType<typeof trpc.googleCalendar.syncNow.useMutation>;
  disconnect: ReturnType<typeof trpc.googleCalendar.disconnect.useMutation>;
  updateSettings: ReturnType<typeof trpc.googleCalendar.updateSettings.useMutation>;
  onReconectar: () => void;
}) {
  const [abrirCalendarios, setAbrirCalendarios] = useState(false);
  // Lista só quando a pessoa abre o seletor: é uma chamada ao Google.
  const calendarios = trpc.googleCalendar.listCalendars.useQuery(undefined, { enabled: abrirCalendarios });
  const falhou = s.lastSyncStatus === "failed";

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            {falhou ? <XCircle className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            <span className="font-medium text-sm">{s.googleEmail ? `Conectado como ${s.googleEmail}` : "Conectado"}</span>
            <Badge variant="outline" className="text-[10px] gap-1"><GoogleCalendarIcon className="h-3 w-3" /> Google Agenda</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {s.lastSyncAt
              ? `Última sincronização ${new Date(s.lastSyncAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}`
              : "Ainda não sincronizado"}
            {" · "}atualiza a cada 10 minutos
          </p>
          {falhou && s.lastSyncError && <p className="text-xs text-red-700 dark:text-red-300">{s.lastSyncError}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={syncNow.isPending} onClick={() => syncNow.mutate()}>
            {syncNow.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Sincronizar agora
          </Button>
          {falhou && (
            <Button size="sm" variant="outline" onClick={onReconectar}>Reconectar</Button>
          )}
          <Button size="sm" variant="ghost" className="text-destructive" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
            <Unplug className="h-3.5 w-3.5 mr-1.5" /> Desconectar
          </Button>
        </div>
      </div>

      <div className="rounded-md border p-3 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="space-y-0.5">
            <Label className="text-sm">Calendário</Label>
            <p className="text-xs text-muted-foreground">Qual dos seus calendários do Google é lido e recebe as reuniões.</p>
          </div>
          <Select
            value={s.calendarId}
            onOpenChange={(aberto) => aberto && setAbrirCalendarios(true)}
            onValueChange={(calendarId) => calendarId !== s.calendarId && updateSettings.mutate({ calendarId })}
          >
            <SelectTrigger className="h-8 w-64">
              <SelectValue placeholder="Principal" />
            </SelectTrigger>
            <SelectContent>
              {/* O valor atual precisa existir na lista antes de o Google responder. */}
              {!calendarios.data?.some((c) => c.id === s.calendarId) && (
                <SelectItem value={s.calendarId}>{s.calendarId === "primary" ? "Principal" : s.calendarId}</SelectItem>
              )}
              {calendarios.isLoading && abrirCalendarios && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">Carregando…</div>
              )}
              {(calendarios.data ?? []).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.summary}{c.primary ? " (principal)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="space-y-0.5">
            <Label htmlFor="google-titulos" className="text-sm">Mostrar títulos dos compromissos</Label>
            <p className="text-xs text-muted-foreground">
              Desligado, quem vê sua agenda lê só “Compromisso no Google Agenda”. Ligado, vê o título do evento.
            </p>
          </div>
          <Switch
            id="google-titulos"
            checked={s.showEventTitles}
            disabled={updateSettings.isPending}
            onCheckedChange={(showEventTitles) => updateSettings.mutate({ showEventTitles })}
          />
        </div>
      </div>
    </div>
  );
}
